import { useCallback, useEffect, useRef } from 'react';

interface UseEventSourceProps {
  url: string;
}

export const useEventSource = <T = any>({ url }: UseEventSourceProps) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(false);

  /**
   * Clean up existing EventSource connection
   */
  const cleanupEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Establish SSE connection to the backend
   */
  const connect = useCallback(() => {
    cleanupEventSource();

    if (!isMountedRef.current) {
      return;
    }

    try {
      // Create EventSource with credentials to send cookies
      const eventSource = new EventSource(url, {
        withCredentials: true,
      });

      eventSourceRef.current = eventSource;

      // Handle errors and reconnection
      eventSource.onerror = (error) => {
        cleanupEventSource();

        // Attempt to reconnect after 5 seconds if still mounted
        if (isMountedRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        }
        console.error('SSE connection error:', error);
      };
    } catch (error) {
      // Retry connection after 5 seconds
      console.error('Failed to connect to SSE:', error);
      if (isMountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    }
  }, [isMountedRef, url, cleanupEventSource]);

  const addListener = useCallback((onMessage: (data: T) => void) => {
    const listenerFunc = (event: MessageEvent) => {
      try {
        const parsedData: T = JSON.parse(event.data);
        onMessage(parsedData);
      } catch {
        console.error('Failed to parse SSE message data:', event.data);
      }
    };
    eventSourceRef.current?.addEventListener('message', listenerFunc);
    return () => eventSourceRef.current?.removeEventListener('message', listenerFunc);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    connect();
    return () => {
      isMountedRef.current = false;
      cleanupEventSource();
    };
  }, [connect, cleanupEventSource]);

  return { addListener };
};
